/**
 * Cross-Tab Session Management Hook
 *
 * Manages session synchronization across browser tabs using:
 * - BroadcastChannel for real-time communication
 * - localStorage events as fallback
 * - Visibility/focus events for session validity checks
 *
 * Note: Multiple concurrent login sessions are allowed (for 30-day persistence across devices).
 * This hook only invalidates sessions when:
 * - User explicitly logs out in another tab
 * - JWT token expires (checked periodically and on visibility change)
 * Single-game-at-a-time is enforced at the game_session level, not here.
 */

import { useEffect, useRef, useCallback } from 'react';
import { apiClient } from '@/lib/api-client';
import { storage } from '@/lib/storage';

interface UseCrossTabSessionOptions {
  /** User ID - required to enable session management */
  userId: string | null;
  /** Session token - required to enable session management */
  sessionToken: string | null;
  /** Callback when session is invalidated */
  onSessionInvalidated: (message: string) => void;
}

interface UseCrossTabSessionReturn {
  /** Check if current session is still valid */
  checkSessionValidity: () => Promise<boolean>;
}

/** Channel name for cross-tab communication */
const BROADCAST_CHANNEL_NAME = 'auth-session';

/** Minimum interval between session validity checks (ms) */
const SESSION_CHECK_THROTTLE_MS = 5000;

/** Periodic heartbeat interval (ms) */
const HEARTBEAT_INTERVAL_MS = 10000;

/** Periodic session validity check interval (ms) */
const SESSION_CHECK_INTERVAL_MS = 30000;

export function useCrossTabSession({
  userId,
  sessionToken,
  onSessionInvalidated,
}: UseCrossTabSessionOptions): UseCrossTabSessionReturn {
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const sessionCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastSessionCheckRef = useRef<number>(0);

  /**
   * Check if current session is still valid
   */
  const checkSessionValidity = useCallback(async (): Promise<boolean> => {
    try {
      // Use combined session endpoint for single API call
      const response = await apiClient.get('/auth/session');
      if (!response.valid) {
        onSessionInvalidated(
          response.message || 'Your session has expired. Please log in again.'
        );
        return false;
      }
      return true;
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.message?.includes('Session')) {
        onSessionInvalidated('Your session has expired. Please log in again.');
        return false;
      }
      return true; // Assume valid if we can't determine
    }
  }, [onSessionInvalidated]);

  /**
   * Listen for localStorage changes from other tabs
   */
  useEffect(() => {
    if (!userId || !sessionToken) {
      return;
    }

    const handleStorageChange = async (e: StorageEvent) => {
      // React to auth_token changes from other tabs
      // Note: We allow multiple concurrent sessions, so we only react to logouts (token removal)
      if (e.key === 'auth_token' && e.newValue !== e.oldValue) {
        const currentToken = sessionToken;
        const oldToken = e.oldValue;

        // Token was removed (logout in another tab) - check if our session is still valid
        if (!e.newValue && currentToken && oldToken === currentToken) {
          setTimeout(async () => {
            await checkSessionValidity();
          }, 1000);
        }
        // New login in another tab - no action needed, multiple sessions are allowed
      }
      // session_id changes are informational only - multiple sessions are allowed
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [userId, sessionToken, checkSessionValidity]);

  /**
   * Use BroadcastChannel for cross-tab communication
   */
  useEffect(() => {
    if (!userId || !sessionToken) {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }
      return;
    }

    // Create broadcast channel
    let channel: BroadcastChannel | null = null;
    try {
      if (!broadcastChannelRef.current && typeof BroadcastChannel !== 'undefined') {
        broadcastChannelRef.current = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      }
      channel = broadcastChannelRef.current;
    } catch (error) {
      channel = null;
    }

    const handleMessage = async (event: MessageEvent) => {
      // Note: Multiple concurrent sessions are allowed, so we don't invalidate
      // based on different sessionIds. Heartbeats are informational only.
      // Session validity is checked periodically and on visibility change.
      if (event.data.type === 'session-logout') {
        // Another tab explicitly logged out - check if our session is affected
        const now = Date.now();
        if (now - lastSessionCheckRef.current > SESSION_CHECK_THROTTLE_MS) {
          lastSessionCheckRef.current = now;
          await checkSessionValidity();
        }
      }
    };

    const broadcastHeartbeat = () => {
      if (!channel) return;
      const sessionId = storage.getItem('session_id');
      if (sessionId) {
        try {
          channel.postMessage({
            type: 'session-heartbeat',
            sessionId,
            timestamp: Date.now(),
          });
        } catch (error) {
          // Channel might be closed
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (channel) {
          broadcastHeartbeat();
        }
        checkSessionValidity();
      }
    };

    const handleFocus = () => {
      if (channel) {
        broadcastHeartbeat();
      }
      checkSessionValidity();
    };

    if (channel) {
      channel.addEventListener('message', handleMessage);
      broadcastHeartbeat();
      const heartbeatInterval = setInterval(broadcastHeartbeat, HEARTBEAT_INTERVAL_MS);

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleFocus);

      return () => {
        channel?.removeEventListener('message', handleMessage);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocus);
        clearInterval(heartbeatInterval);
      };
    } else {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleFocus);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocus);
      };
    }
  }, [userId, sessionToken, checkSessionValidity]);

  /**
   * Poll session validity as fallback
   * Note: We skip the initial check since AuthContext already verifies the session on mount
   */
  useEffect(() => {
    if (!userId || !sessionToken) {
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
        sessionCheckIntervalRef.current = null;
      }
      return;
    }

    // Skip initial check - AuthContext already verified the session
    // Only set up periodic polling for ongoing validity checks
    sessionCheckIntervalRef.current = setInterval(checkSessionValidity, SESSION_CHECK_INTERVAL_MS);

    return () => {
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
        sessionCheckIntervalRef.current = null;
      }
    };
  }, [userId, sessionToken, checkSessionValidity]);

  return {
    checkSessionValidity,
  };
}
